import { Controller } from '@hotwired/stimulus'

/**
 * Stimulus-контроллер для карточных слайдеров на Swiper.
 *
 * Лениво загружает Swiper и его модули, скрывает навигацию до завершения
 * инициализации, настраивает адаптивное количество карточек и уничтожает
 * созданный инстанс при отключении контроллера.
 *
 * @extends {Controller}
 */
export default class SwiperController extends Controller {
  /**
   * Тексты для a11y-сообщений кнопок навигации.
   *
   * @type {{ prevText: { type: StringConstructor }, nextText: { type: StringConstructor } }}
   */
  static values = {
    prevText: {
      type: String
    },
    nextText: {
      type: String
    }
  }

  /**
   * DOM-цели контроллера.
   *
   * `container` — корневой элемент Swiper, `navigation` — обертка кнопок,
   * `prevBtn` и `nextBtn` — кнопки перехода между слайдами.
   *
   * @type {string[]}
   */
  static targets = ['container', 'navigation', 'prevBtn', 'nextBtn']

  /**
   * Активный инстанс Swiper.
   *
   * @type {import('swiper').default | null}
   */
  #swiper = null

  /**
   * Идентификатор последней попытки инициализации.
   *
   * Нужен, чтобы игнорировать результат устаревшего асинхронного импорта после
   * Turbo-перехода или повторного подключения контроллера.
   *
   * @type {number}
   */
  #initRunId = 0

  /**
   * Кеш результата динамического импорта Swiper и его модулей.
   *
   * @type {{ Swiper: import('swiper').default, Navigation: unknown, A11y: unknown } | null}
   */
  static #swiperCache = null

  /**
   * Запускает асинхронную инициализацию слайдера при подключении контроллера.
   */
  connect() {
    this.#initRunId += 1
    this.#initCards(this.#initRunId)
  }

  /**
   * Отменяет ожидающую инициализацию и уничтожает текущий инстанс Swiper.
   */
  disconnect() {
    this.#initRunId += 1
    this.#swiper?.destroy(true, true)
    this.#swiper = null
  }

  /**
   * Создает Swiper для карточек и включает навигацию после готовности.
   *
   * @param {number} runId — идентификатор текущей попытки инициализации
   * @returns {Promise<void>}
   */
  async #initCards(runId) {
    this.#setNavVisible(false)
    const { Swiper, Navigation, A11y } = await this.#importSwiper()

    if (!this.element.isConnected || runId !== this.#initRunId) return

    const breakpoints = {
      768: { slidesPerView: 2, spaceBetween: 0 },
      1024: { slidesPerView: 3, spaceBetween: 16 },
      1280: { slidesPerView: 4, spaceBetween: 24 }
    }

    this.#swiper = new Swiper(this.containerTarget, {
      modules: [Navigation, A11y],
      speed: 800,
      slidesPerView: 'auto',
      spaceBetween: 0,
      allowTouchMove: true,
      grabCursor: true,
      a11y: {
        prevSlideMessage: this.prevTextValue,
        nextSlideMessage: this.nextTextValue
      },
      navigation: {
        nextEl: this.hasNextBtnTarget ? this.nextBtnTarget : null,
        prevEl: this.hasPrevBtnTarget ? this.prevBtnTarget : null
      },
      breakpoints,
      on: { afterInit: () => this.#setNavVisible(true) }
    })
  }

  /**
   * Показывает или скрывает контейнер навигации, если он есть в разметке.
   *
   * @param {boolean} visible — нужно ли показывать навигацию
   */
  #setNavVisible(visible) {
    if (this.hasNavigationTarget) {
      this.navigationTarget.style.display = visible ? '' : 'none'
    }
  }

  /**
   * Загружает Swiper и его модули один раз на страницу.
   *
   * @returns {Promise<{ Swiper: import('swiper').default, Navigation: unknown, A11y: unknown }>}
   */
  async #importSwiper() {
    if (!SwiperController.#swiperCache) {
      const [{ default: Swiper }, modules] = await Promise.all([
        import('swiper'),
        // eslint-disable-next-line import/no-unresolved
        import('swiper/modules')
      ])
      SwiperController.#swiperCache = { Swiper, ...modules }
    }
    return SwiperController.#swiperCache
  }
}
